import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { Slider } from "primereact/slider";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { WeekPicker } from "./WeekPicker";
import { SmartProjectSelector } from "./SmartProjectSelector";
import { SmartEmployeeSelector } from "./SmartEmployeeSelector";
import { FloatLabel } from "primereact/floatlabel";
import { apiService, SchedulePatch } from "../services/apiService";
import { useActivitiesContext } from "../contexts/ActivitiesContext";
import {
  getCurrentWeek,
  formatWeek,
  validateWeekRange,
} from "../utils/weekUtils";

interface ScheduleEntry {
  id?: string;
  projectId: string;
  employeeIds: number[];
  weekNumber: number;
  year: number;
  hours: number;
  comment?: string;
  // For multi-week mode
  endWeekNumber?: number;
  endYear?: number;
  recurrenceWeeks?: number; // How many weeks between recurrences
}

interface SchedulingModalProps {
  visible: boolean;
  onHide: () => void;
  onSave: (entry: ScheduleEntry) => void;
  onDelete?: (entryId: string) => void;
  onSchedulePatch?: (patch: SchedulePatch) => void;

  // Pre-filled values
  initialData?: {
    projectId?: string;
    employeeIds?: number[];
    weekNumber?: number;
    year?: number;
    hours?: number;
    comment?: string;
    entryId?: string; // For editing existing entries
    activityId?: number; // Activity ID for API calls
    originalWeekNumber?: number; // Original week for edits
    originalYear?: number; // Original year for edits
    isExisting?: boolean; // Flag to indicate this is an existing entry
  };
}

export const SchedulingModal: React.FC<SchedulingModalProps> = ({
  visible,
  onHide,
  onSave,
  onDelete,
  onSchedulePatch,
  initialData,
}) => {
  // Get data from contexts
  const { activities } = useActivitiesContext();

  // Transform context data to match component expectations
  const projects = activities
    .filter((activity) => activity.project && activity.project.client) // Filter out activities without project data
    .map((activity) => ({
      id: activity.id.toString(),
      activityId: activity.id,
      name: activity.name,
      activity: activity.name,
      client: activity.project!.client.name,
      project: activity.project!.name,
    }));

  // Form state
  const [selectedProject, setSelectedProject] = useState<string>("");
  const [selectedEmployees, setSelectedEmployees] = useState<number[]>([]);
  const [weekNumber, setWeekNumber] = useState<number>(getCurrentWeek());
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [hours, setHours] = useState<number>(8);
  const [comment, setComment] = useState<string>("");

  // Multi-week mode
  const [multiWeekMode, setMultiWeekMode] = useState<boolean>(false);
  const [endWeekNumber, setEndWeekNumber] = useState<number>(getCurrentWeek());
  const [endYear, setEndYear] = useState<number>(new Date().getFullYear());
  const [recurrenceWeeks, setRecurrenceWeeks] = useState<number>(1);

  // Form validation
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Reset form when modal opens/closes or initial data changes
  useEffect(() => {
    if (visible) {
      // Pre-fill with initial data if provided
      setSelectedProject(initialData?.projectId || "");
      setSelectedEmployees(initialData?.employeeIds || []);
      setWeekNumber(initialData?.weekNumber || getCurrentWeek());
      setYear(initialData?.year || new Date().getFullYear());
      setHours(initialData?.hours || 8);
      setComment(initialData?.comment || "");
      setMultiWeekMode(false);
      setEndWeekNumber(initialData?.weekNumber || getCurrentWeek());
      setEndYear(initialData?.year || new Date().getFullYear());
      setRecurrenceWeeks(1);
      setErrors({});
    }
  }, [visible, initialData]);

  // Validation
  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!selectedProject && !isExisting) {
      newErrors.project = "Please select a project";
    }

    if (selectedEmployees.length === 0 && !isExisting) {
      newErrors.employees = "Please select at least one employee";
    }

    if (hours <= 0 || hours > 80) {
      newErrors.hours = "Hours must be between 1 and 80";
    }

    if (multiWeekMode) {
      if (!validateWeekRange(year, weekNumber, endYear, endWeekNumber)) {
        newErrors.endWeek = "End week must be after start week";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle save
  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    const week = formatWeek(year, weekNumber);
    const isExisting = !!initialData?.isExisting;

    try {
      if (isExisting) {
        // Update existing entry
        if (selectedEmployees.length === 1 && initialData?.activityId != null) {
          const originalWeek = formatWeek(
            initialData.originalYear || year,
            initialData.originalWeekNumber || weekNumber,
          );

          const clientMutationId = crypto.randomUUID();
          const response = await apiService.updateEvent(
            {
              person: selectedEmployees[0],
              hours,
              activity: initialData.activityId,
              week,
              original_week: originalWeek,
              comment: comment || undefined,
            },
            clientMutationId,
          );

          if (!response.success) {
            console.error("Update failed:", response.message);
            return;
          }

          if (response.data && onSchedulePatch) {
            onSchedulePatch({
              ...response.data,
              client_mutation_id:
                response.data.client_mutation_id ?? clientMutationId,
            });
          } else if (onSchedulePatch) {
            onSchedulePatch({
              type: "schedule.patch",
              origin: "client",
              client_mutation_id: clientMutationId,
              changes: [],
            });
          }
        } else {
          console.error("Update conditions not met:");
        }
      } else {
        // Create new entry
        // For new entries, get activityId from the selected project
        const selectedProjectData = projects.find(
          (p) => p.id === selectedProject,
        );
        const activityId =
          initialData?.activityId ?? selectedProjectData?.activityId;

        if (activityId != null && selectedEmployees.length > 0) {
          const data: any = {
            people: selectedEmployees,
            activity: activityId,
            hours,
            week,
            comment: comment || undefined,
          };

          if (multiWeekMode) {
            const endWeek = formatWeek(endYear, endWeekNumber);
            data.recurrence = {
              endweek: endWeek,
              weeks: recurrenceWeeks,
            };
          }

          const clientMutationId = crypto.randomUUID();
          const response = await apiService.createEvent(data, clientMutationId);

          if (!response.success) {
            console.error("Create failed:", response.message);
            return;
          }

          if (response.data && onSchedulePatch) {
            onSchedulePatch({
              ...response.data,
              client_mutation_id:
                response.data.client_mutation_id ?? clientMutationId,
            });
          } else if (onSchedulePatch) {
            onSchedulePatch({
              type: "schedule.patch",
              origin: "client",
              client_mutation_id: clientMutationId,
              changes: [],
            });
          }
        }
      }

      // Create legacy entry object for the parent component
      const entry: ScheduleEntry = {
        id: initialData?.entryId,
        projectId: selectedProject,
        employeeIds: selectedEmployees,
        weekNumber,
        year,
        hours,
        comment: comment || undefined,
      };

      if (multiWeekMode) {
        entry.endWeekNumber = endWeekNumber;
        entry.endYear = endYear;
        entry.recurrenceWeeks = recurrenceWeeks;
      }

      onSave(entry);
      onHide();
    } catch (error) {
      console.error("Error saving entry:", error);
      // You might want to show an error message to the user here
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (
      initialData?.isExisting &&
      initialData?.activityId != null &&
      selectedEmployees.length === 1
    ) {
      try {
        const week = formatWeek(year, weekNumber);

        const clientMutationId = crypto.randomUUID();
        const response = await apiService.deleteEvent(
          {
            week,
            activity: initialData.activityId,
            person: selectedEmployees[0],
          },
          clientMutationId,
        );

        if (response.success) {
          if (response.data && onSchedulePatch) {
            onSchedulePatch({
              ...response.data,
              client_mutation_id:
                response.data.client_mutation_id ?? clientMutationId,
            });
          } else if (onSchedulePatch) {
            onSchedulePatch({
              type: "schedule.patch",
              origin: "client",
              client_mutation_id: clientMutationId,
              changes: [],
            });
          }

          if (initialData?.entryId && onDelete) {
            onDelete(initialData.entryId);
          }
          onHide();
        } else {
          console.error("Delete failed:", response.message);
          // You might want to show an error message to the user here
        }
      } catch (error) {
        console.error("Error deleting entry:", error);
        // You might want to show an error message to the user here
      }
    }
  };

  const isExisting = !!initialData?.isExisting;

  return (
    <Dialog
      header={isExisting ? "Edit Schedule Entry" : "New Schedule Entry"}
      visible={visible}
      style={{ width: "650px", minHeight: "600px" }}
      onHide={onHide}
      resizable={false}
      footer={
        <div className="flex justify-content-between">
          <div>
            {isExisting && onDelete && (
              <Button
                label="Delete"
                icon="pi pi-trash"
                className="p-button-danger p-button-outlined"
                onClick={handleDelete}
              />
            )}
          </div>
          <div className="flex gap-2">
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-outlined"
              onClick={onHide}
            />
            <Button label="Save" icon="pi pi-check" onClick={handleSave} />
          </div>
        </div>
      }
      closable={true}
      modal={true}
    >
      <div className="grid">
        {/* Multi-week toggle */}
        {!isExisting ? (
          <div className="col-12">
            <div className="field">
              <div className="flex align-items-center gap-2">
                <InputSwitch
                  id="multi-week"
                  checked={multiWeekMode}
                  onChange={(e) => setMultiWeekMode(e.value)}
                />
                <label htmlFor="multi-week" className="text-sm font-medium">
                  <span className="pi pi-sync"></span> Reoccurrence
                </label>
              </div>
            </div>
          </div>
        ) : null}

        {/* Week Selection */}
        <div className="col-12">
          <div className="field">
            {!multiWeekMode ? (
              <FloatLabel>
                <WeekPicker
                  value={weekNumber}
                  year={year}
                  onChange={(week, selectedYear) => {
                    setWeekNumber(week);
                    setYear(selectedYear);
                  }}
                />
                <label className="block text-sm font-medium mb-2">Week *</label>
              </FloatLabel>
            ) : (
              <div>
                <div className="grid">
                  {/* Start Week */}
                  <div className="col-6">
                    <FloatLabel>
                      <WeekPicker
                        value={weekNumber}
                        year={year}
                        onChange={(week, selectedYear) => {
                          setWeekNumber(week);
                          setYear(selectedYear);
                        }}
                      />
                      <label className="block text-sm font-medium">
                        Start Week
                      </label>
                    </FloatLabel>
                  </div>

                  {/* End Week */}
                  <div className="col-6">
                    <FloatLabel>
                      <WeekPicker
                        value={endWeekNumber}
                        year={endYear}
                        minWeek={weekNumber}
                        minYear={year}
                        onChange={(week, selectedYear) => {
                          setEndWeekNumber(week);
                          setEndYear(selectedYear);
                        }}
                      />
                      <label className="block text-sm font-medium">
                        End Week
                      </label>
                    </FloatLabel>
                  </div>

                  {/* Recurrence Slider */}
                  <div className="col-12">
                    <div className="field mt-3">
                      <label className="block text-sm font-medium mb-2">
                        Recur every {recurrenceWeeks} week
                        {recurrenceWeeks !== 1 ? "s" : ""}
                      </label>
                      <div className="flex align-items-center gap-3">
                        <span className="text-sm">1</span>
                        <Slider
                          value={recurrenceWeeks}
                          onChange={(e) =>
                            setRecurrenceWeeks(
                              Array.isArray(e.value) ? e.value[0] : e.value,
                            )
                          }
                          min={1}
                          max={12}
                          step={1}
                          className="flex-1"
                        />
                        <span className="text-sm">12</span>
                      </div>
                    </div>
                  </div>
                </div>
                {errors.endWeek && (
                  <small className="p-error">{errors.endWeek}</small>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Hours */}
        <div className="col-12">
          <div className="field">
            <div className="flex align-items-center gap-3">
              {/* <div className='flex align-items-center'> */}

              <FloatLabel>
                <InputNumber
                  value={hours}
                  onValueChange={(e) => setHours(e.value || 0)}
                  min={1}
                  max={40}
                  step={1}
                  suffix=" h"
                  onFocus={(e) => {
                    // Select all text when the input gains focus
                    setTimeout(() => {
                      e.target.select();
                    }, 0);
                  }}
                />
                <label
                  htmlFor="hours"
                  className="block text-sm font-medium mb-2"
                >
                  Hours *
                </label>
              </FloatLabel>

              <Slider
                id="hours"
                value={hours}
                onChange={(e) =>
                  setHours(Array.isArray(e.value) ? e.value[0] : e.value)
                }
                min={1}
                max={40}
                step={1}
                className="flex-1"
              />
            </div>
            {errors.hours && <small className="p-error">{errors.hours}</small>}
          </div>
        </div>

        {/* Project Selection */}
        <div className="col-12">
          <SmartProjectSelector
            value={selectedProject}
            onChange={setSelectedProject}
            disabled={isExisting}
            error={errors.project}
          />
        </div>

        {/* Employee Selection */}
        <div className="col-12">
          <SmartEmployeeSelector
            value={selectedEmployees}
            onChange={setSelectedEmployees}
            disabled={isExisting}
            error={errors.employees}
          />
        </div>

        {/* Comment */}
        <div className="col-12">
          <div className="field">
            <FloatLabel>
              <InputTextarea
                id="comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={3}
                placeholder="Optional comment..."
                className="w-full"
              />
              <label
                htmlFor="comment"
                className="block text-sm font-medium mb-2"
              >
                Comment
              </label>
            </FloatLabel>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
